import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    useToast,
    Text,
    Icon,
    Box
} from '@chakra-ui/react';
import { FiCheckCircle } from 'react-icons/fi';
import registrationService from '../services/registrationService';

const RegistrationModal = ({ isOpen, onClose, niveau }) => {
    const [formData, setFormData] = useState({
        parentName: '',
        studentName: '',
        parentPhone: '',
        studentPhone: '',
        email: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const toast = useToast();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.parentName || !formData.studentName || !formData.parentPhone || !formData.email) {
            toast({
                title: 'يرجى ملء جميع الحقول المطلوبة',
                status: 'warning',
                duration: 3000
            });
            return;
        }

        setLoading(true);
        try {
            await registrationService.createRegistration({
                ...formData,
                niveau: niveau._id
            });

            setSubmitted(true);
            toast({
                title: 'تم إرسال طلب التسجيل بنجاح',
                description: 'سنتواصل معك قريباً',
                status: 'success',
                duration: 5000
            });

            // Reset form after 2 seconds
            setTimeout(() => {
                setFormData({
                    parentName: '',
                    studentName: '',
                    parentPhone: '',
                    studentPhone: '',
                    email: ''
                });
                setSubmitted(false);
                onClose();
            }, 2000);
        } catch (error) {
            toast({
                title: 'خطأ في إرسال الطلب',
                description: error.response?.data?.message || 'حدث خطأ',
                status: 'error',
                duration: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
            <ModalOverlay backdropFilter="blur(5px)" />
            <ModalContent dir="rtl" borderRadius="2xl">
                <ModalHeader
                    fontFamily="Cairo, sans-serif"
                    fontSize="2xl"
                    borderBottom="1px"
                    borderColor="gray.200"
                    pb={4}
                >
                    {submitted ? 'تم الإرسال بنجاح!' : `التسجيل في ${niveau?.name}`}
                </ModalHeader>
                <ModalCloseButton left="12px" right="unset" />

                <ModalBody py={6}>
                    {submitted ? (
                        <VStack spacing={6} py={8}>
                            <Icon as={FiCheckCircle} boxSize={20} color="green.500" />
                            <Text
                                fontSize="xl"
                                fontFamily="Cairo, sans-serif"
                                textAlign="center"
                                color="gray.700"
                            >
                                تم استلام طلبك بنجاح
                                <br />
                                سنتواصل معك في أقرب وقت
                            </Text>
                        </VStack>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <VStack spacing={5}>
                                <FormControl isRequired>
                                    <FormLabel fontFamily="Cairo, sans-serif" fontWeight="600">
                                        اسم ولي الأمر
                                    </FormLabel>
                                    <Input
                                        name="parentName"
                                        value={formData.parentName}
                                        onChange={handleChange}
                                        placeholder="أدخل اسم ولي الأمر"
                                        size="lg"
                                        borderRadius="lg"
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel fontFamily="Cairo, sans-serif" fontWeight="600">
                                        اسم الطالب
                                    </FormLabel>
                                    <Input
                                        name="studentName"
                                        value={formData.studentName}
                                        onChange={handleChange}
                                        placeholder="أدخل اسم الطالب"
                                        size="lg"
                                        borderRadius="lg"
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel fontFamily="Cairo, sans-serif" fontWeight="600">
                                        رقم هاتف ولي الأمر
                                    </FormLabel>
                                    <Input
                                        name="parentPhone"
                                        value={formData.parentPhone}
                                        onChange={handleChange}
                                        placeholder="أدخل رقم الهاتف"
                                        type="tel"
                                        size="lg"
                                        borderRadius="lg"
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontFamily="Cairo, sans-serif" fontWeight="600">
                                        رقم هاتف الطالب (اختياري)
                                    </FormLabel>
                                    <Input
                                        name="studentPhone"
                                        value={formData.studentPhone}
                                        onChange={handleChange}
                                        placeholder="أدخل رقم الهاتف"
                                        type="tel"
                                        size="lg"
                                        borderRadius="lg"
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel fontFamily="Cairo, sans-serif" fontWeight="600">
                                        البريد الإلكتروني
                                    </FormLabel>
                                    <Input
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="example@email.com"
                                        type="email"
                                        size="lg"
                                        borderRadius="lg"
                                    />
                                </FormControl>
                            </VStack>
                        </form>
                    )}
                </ModalBody>

                {!submitted && (
                    <ModalFooter borderTop="1px" borderColor="gray.200" pt={4}>
                        <Button
                            variant="ghost"
                            ml={3}
                            onClick={onClose}
                            fontFamily="Cairo, sans-serif"
                        >
                            إلغاء
                        </Button>
                        <Button
                            bg={niveau?.color || 'blue.500'}
                            color="white"
                            _hover={{ bg: `${niveau?.color}DD` || 'blue.600' }}
                            onClick={handleSubmit}
                            isLoading={loading}
                            loadingText="جاري الإرسال..."
                            fontFamily="Cairo, sans-serif"
                            fontWeight="bold"
                            size="lg"
                            px={8}
                        >
                            إرسال الطلب
                        </Button>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
};

export default RegistrationModal;
